import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Icon } from '@iconify/react';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import {
  Divider,
  Button,
  Select,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardBody,
  Input,
  Link,
  Switch,
  Tabs,
  Tab,
  Progress,
  Chip,
} from '@nextui-org/react';
import TopLoadingBar from '../../../components/ui/TopLoadingBar';
import { Settlement } from 'common/src/utils/types';
import {
  getSettlementDistribution,
  getExporterGlobalFobPercentile,
  getSettlementSummaryKPIs,
  getSettlementSummaryKPIsV2,
  getSettlementSummaryRPC,
  getSettlementSummaryRPCV2,
  getShipmentSettlementCounts,
  getExporterGlobalFobPercentileV2,
  getSettlementDistributionV2,
  ExporterGlobalFobPercentile,
  SettlementDistributionResult,
  SettlementSummaryKPIs,
  ShipmentSettlementCounts,
} from 'common/src/api/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import {
  calculateFOBSettlement,
  filterSettlementDetails,
  formatMoneyUSD,
  formatNumber,
} from '../../../utils/functions';
import ClientSettlementsTable from '../../../components/settlements/client-settlements-table';
import ClientCostsTable from '../../../components/settlements/client-costs-table';
import ExchangeRatesTable from '../../../components/settlements/exchange-rates-table';
import ExchangeRateEvolutionChart from '../../../components/settlements/chart-exchange-rate-evolution';
import FlowsTable from '../../../components/settlements/flows-table';
import SettlementsVarietyFOBTable from '../../../components/settlements/fob-cq-vairieties-table';
import { logUserAction } from '../../../services/actionsService';
import { sortWeekKeys } from 'common/src/utils/functions';
import { formatXAxisLabel } from '../../../components/specifics/settlements-client-comparision/ShipmentBarChart';
import {
  groupSettlementsByShip,
  groupSettlementsByWeek,
} from 'common/src/utils/functions';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import ChartsSection from '../../../sections/settlements/summary/ChartsSection';
import RankingClientsTable from '../../../components/settlements/ranking-clients-table';
import ClientComparativeSaldosTable from '../../../components/settlements/client-comparative-saldos-table';
import FOBAccumulatedCaliberColorTable from '../../../components/settlements/fob-accumulated-caliber-color-table';
import FOBAccumulatedWeekVariety from '../../../components/settlements/fob-accumulated-week-variety';
import ReceiverFilter from '../../../../../common/src/components/filters/ReceiverFilter';
import MarketFilter from '../../../../../common/src/components/filters/MarketFilter';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import UnitPriceFilter from 'common/src/components/filters/UnitPriceFilter';
import CostFilterType from '../../../../../common/src/components/filters/CostFilterType';
import Header from '../../../components/layouts/Header';
import DistributionBarChart from '../../../components/charts/DistributionBarChart';
import ChartsFiltersSection from '../../../sections/settlements/summary/ChartsFiltersSection';
import { ChartFilters } from '../../../utils/functions';
import { TableExportRef } from '../../../utils/table-styles';
import ExcelExportButton from '../../../components/ui/ExcelExportButton';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  PageLayout,
} from '../../../components/ui';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';

type SinisterChoice = 'all' | 'sinister' | 'non_sinister';
type DateTypeChoice = 'eta' | 'etd' | 'sale_date';
type ViewModeChoice = 'ship' | 'week';

interface ActiveFilters {
  sinister: SinisterChoice;
  receivers: string[]; // [] = all
  markets: string[]; // [] = all
  cost: string;
  dateType: DateTypeChoice;
  viewMode: ViewModeChoice;
  selectedItems: string[]; // ['all'] = all
}

const DEFAULT_FILTERS: ActiveFilters = {
  sinister: 'all',
  // 'all' sentinel = no narrowing (show everything). [] = explicit deselect-all
  // (user wants zero results). ['A','B'] = subset.
  receivers: ['all'],
  markets: ['all'],
  cost: 'all',
  dateType: 'eta',
  viewMode: 'ship',
  selectedItems: ['all'],
};

const MISSING_BUSINESS_TYPE = 'Sin Tipo de Negocio';
const MISSING_MARKET = 'Sin Mercado';
const MISSING_MARKET_ALIASES = new Set([MISSING_MARKET, 'No Market']);

const shouldUseDetailEconomicsV2 = () => {
  if (typeof window === 'undefined') return true;
  const economics = new URLSearchParams(window.location.search)
    .get('economics')
    ?.toLowerCase();
  return !['legacy', 'v1', 'old'].includes(economics || '');
};

const normalizeMarketFilterValue = (value: string): string => {
  const trimmed = value.trim();
  return MISSING_MARKET_ALIASES.has(trimmed) ? MISSING_MARKET : trimmed;
};

const normalizeMarketFilterValues = (values: string[]): string[] => {
  if (values.includes('all')) return ['all'];
  return values.map(normalizeMarketFilterValue);
};

const FILTER_URL_KEYS = [
  'sinister',
  'receivers',
  'markets',
  'cost',
  'date_type',
  'view_mode',
  'items',
] as const;

const parseMultiParam = (p: URLSearchParams, key: string): string[] => {
  // Absent key = ['all'] (no narrowing). Empty value = [] (explicit deselect).
  // Non-empty list = subset.
  if (!p.has(key)) return ['all'];
  const raw = p.get(key) || '';
  return raw ? raw.split(',').filter(Boolean) : [];
};

const parseFiltersFromURL = (search: string): ActiveFilters => {
  const p = new URLSearchParams(search);
  const items = p.get('items')?.split(',').filter(Boolean) || [];
  return {
    sinister: (p.get('sinister') as SinisterChoice) || 'all',
    receivers: parseMultiParam(p, 'receivers'),
    markets: normalizeMarketFilterValues(parseMultiParam(p, 'markets')),
    cost: p.get('cost') || 'all',
    dateType: (p.get('date_type') as DateTypeChoice) || 'eta',
    viewMode: (p.get('view_mode') as ViewModeChoice) || 'ship',
    selectedItems: items.length > 0 ? items : ['all'],
  };
};

const writeMultiParam = (p: URLSearchParams, key: string, value: string[]) => {
  p.delete(key);
  if (value.includes('all')) return; // no narrowing → absent
  if (value.length === 0) {
    p.set(key, ''); // explicit "none selected"
    return;
  }
  p.set(key, value.join(','));
};

const writeFiltersToURL = (filters: ActiveFilters) => {
  const p = new URLSearchParams(window.location.search);
  FILTER_URL_KEYS.forEach((k) => p.delete(k));
  if (filters.sinister !== 'all') p.set('sinister', filters.sinister);
  writeMultiParam(p, 'receivers', filters.receivers);
  writeMultiParam(p, 'markets', filters.markets);
  if (filters.cost !== 'all') p.set('cost', filters.cost);
  if (filters.dateType !== 'eta') p.set('date_type', filters.dateType);
  if (filters.viewMode !== 'ship') p.set('view_mode', filters.viewMode);
  if (
    filters.selectedItems.length > 0 &&
    !filters.selectedItems.includes('all')
  ) {
    p.set('items', filters.selectedItems.join(','));
  }
  const query = p.toString();
  const newUrl = window.location.pathname + (query ? `?${query}` : '');
  window.history.replaceState(null, '', newUrl);
};

const getPreviousSeasonName = (season?: string | null): string | null => {
  if (!season) return null;
  const parts = season.split('-');
  if (parts.length !== 2) return null;

  const [start, end] = parts;
  const startNumber = Number(start);
  const endNumber = Number(end);
  if (!Number.isFinite(startNumber) || !Number.isFinite(endNumber)) return null;

  const pad = (value: number, width: number) =>
    value.toString().padStart(width, '0');

  return `${pad(startNumber - 1, start.length)}-${pad(endNumber - 1, end.length)}`;
};

const getPreviousSeasonWeekKeys = (
  items: string[],
  season?: string | null
): string[] | undefined => {
  if (!season || items.length === 0 || items.includes('all')) return undefined;
  const weekKeys = items
    .map((item) => {
      const match = item.match(/(\d{4})-W(\d{1,2})/);
      if (!match) return null;
      const [, year, week] = match;
      return `${Number(year) - 1}-${week.padStart(2, '0')}`;
    })
    .filter(Boolean) as string[];

  return weekKeys.length > 0 ? weekKeys : undefined;
};

const formatCompletionPercent = (settled: number, total: number) => {
  if (total <= 0 || settled <= 0) return '0';
  if (settled >= total) return '100';
  const pct = (settled / total) * 100;
  const cappedPct = pct >= 99.95 ? 99.9 : pct;
  const formatted = cappedPct.toFixed(1);
  return formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted;
};

interface FilterChip {
  label: string;
  value: string;
}

const FilteredDataNotice = ({ chips }: { chips: FilterChip[] }) => {
  const { t } = useTranslation('settlements');
  if (chips.length === 0) return null;
  return (
    <Alert variant='warning' showIcon className='mb-3'>
      <p>
        {t(
          'Los filtros que tienes activos son considerados para estos cálculos. Retíralos si es que quieres obtener el análisis completo.'
        )}
      </p>
      <div className='mt-2 flex flex-wrap gap-1.5'>
        {chips.map((chip, i) => (
          <Chip key={i} size='sm' variant='flat'>
            <span className='font-semibold'>{chip.label}:</span>{' '}
            <span>{chip.value}</span>
          </Chip>
        ))}
      </div>
    </Alert>
  );
};

const SettlementsSummaryPage = () => {
  const { t } = useTranslation('settlements');
  const { userProfile, session } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const useDetailEconomicsV2 = useMemo(shouldUseDetailEconomicsV2, []);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [missingDataAlert, setMissingDataAlert] = useState(false);

  // Active filters — the applied set that drives fetches and downstream
  // computations. Mirrored to the URL for deep-linking.
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(() =>
    parseFiltersFromURL(window.location.search)
  );
  // Draft filters — what the sidebar is currently editing. Only promoted to
  // activeFilters when the user clicks Apply.
  const [draftFilters, setDraftFilters] =
    useState<ActiveFilters>(activeFilters);

  const {
    sinister: sinisterFilter,
    receivers: receiverFilter,
    markets: marketFilter,
    cost: costFilter,
    dateType,
    viewMode,
    selectedItems,
  } = activeFilters;
  const canonicalMarketFilter = useMemo(
    () => normalizeMarketFilterValues(marketFilter),
    [marketFilter]
  );
  const [chartsViewMode, setChartsViewMode] = useState<'week' | 'day'>('week');
  const [chartOrTable, setChartOrTable] = useState<'chart' | 'table'>('chart');
  const [chartValueMode, setChartValueMode] = useState<'average' | 'total'>(
    'average'
  );
  const [costValueMode, setCostValueMode] = useState<'average' | 'total'>(
    'average'
  );
  const [kgOrBox, setKgOrBox] = useState<'kg' | 'box'>('box');
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [originalSettlements, setOriginalSettlements] = useState<Settlement[]>(
    []
  );
  const [settlementsForCosts, setSettlementsForCosts] = useState<Settlement[]>(
    []
  );
  const [multiplier, setMultiplier] = useState<number>(1);
  const [groupedSettlements, setGroupedSettlements] = useState<{
    byShip: Record<string, Settlement[]>;
    byWeek: Record<string, Settlement[]>;
  }>({ byShip: {}, byWeek: {} });
  const [loading, setLoading] = useState(true);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [initialKpis, setInitialKpis] = useState<SettlementSummaryKPIs | null>(
    null
  );
  const [globalFobBenchmark, setGlobalFobBenchmark] =
    useState<ExporterGlobalFobPercentile | null>(null);
  const [globalFobBenchmarkLoading, setGlobalFobBenchmarkLoading] =
    useState(false);
  const [previousSeasonDistribution, setPreviousSeasonDistribution] =
    useState<SettlementDistributionResult | null>(null);
  const [previousSeasonKpisLoading, setPreviousSeasonKpisLoading] =
    useState(false);
  const [shipmentSettlementCounts, setShipmentSettlementCounts] =
    useState<ShipmentSettlementCounts | null>(null);
  const [shipmentSettlementCountsLoading, setShipmentSettlementCountsLoading] =
    useState(false);
  const [distributionData, setDistributionData] =
    useState<SettlementDistributionResult | null>(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [showWithRevenues, setShowWithRevenues] = useState(true);
  const [revenuesLoading, setRevenuesLoading] = useState(false);
  const [shipWeekSearch, setShipWeekSearch] = useState('');
  const [distributionTab, setDistributionTab] = useState<string>('client');
  const [flowsTab, setFlowsTab] = useState<string>('client');
  const acumuladoTableRef = useRef<TableExportRef>(null);
  const costsTableRef = useRef<TableExportRef>(null);
  const exchangeRatesTableRef = useRef<TableExportRef>(null);
  const caliberColorTableRef = useRef<TableExportRef>(null);
  const weekVarietyTableRef = useRef<TableExportRef>(null);
  const flowsTableRef = useRef<TableExportRef>(null);
  const [chartFilters, setChartFilters] = useState<ChartFilters>({
    variety: [],
    caliber: [],
    color: ['all'],
    format: [],
    market: [],
    label: [],
    receiver: [],
    fleet: [],
    country: [],
    business_type: [],
    dateType: dateType,
  });

  // Reset all filters when season or species changes. Skip the initial mount
  // so URL-driven filter state (from parseFiltersFromURL) is preserved.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setActiveFilters(DEFAULT_FILTERS);
    setDraftFilters(DEFAULT_FILTERS);
    writeFiltersToURL(DEFAULT_FILTERS);
    setShowWithRevenues(true);
    setDistributionTab('client');
    setFlowsTab('client');
    setChartsViewMode('week');
    setChartOrTable('chart');
    setChartValueMode('average');
    setCostValueMode('average');
    setChartFilters({
      variety: [],
      caliber: [],
      color: ['all'],
      format: [],
      market: [],
      label: [],
      receiver: [],
      fleet: [],
      country: [],
      business_type: [],
      dateType: 'eta',
    });
    setMissingDataAlert(false);
  }, [selectedSeason, selectedSpecies]);

  const handleChartFilterChange = useCallback((
    filterName: string,
    value: string | string[]
  ) => {
    setChartFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  useEffect(() => {
    setChartFilters((prev) => ({ ...prev, dateType }));
  }, [dateType]);

  // All receiver/market values currently in the dataset — used by the modal's
  // multi-select filters to know when "all" is effectively selected (so we can
  // collapse the URL param back to absent).
  const allReceivers = useMemo(
    () => [...new Set(settlements.map((s) => s.receiver))],
    [settlements]
  );
  const allMarkets = useMemo(
    () => [...new Set(settlements.map((s) => s.market?.name || MISSING_MARKET))],
    [settlements]
  );

  const hasDraftChanges = useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(activeFilters),
    [draftFilters, activeFilters]
  );

  // Count of filter fields where draft differs from active. Used by the
  // sidebar's pending-changes banner to tell the user how many unsaved
  // changes they have.
  const draftChangeCount = useMemo(() => {
    const eq = (a: unknown, b: unknown) =>
      JSON.stringify(a) === JSON.stringify(b);
    let n = 0;
    if (draftFilters.sinister !== activeFilters.sinister) n++;
    if (!eq(draftFilters.receivers, activeFilters.receivers)) n++;
    if (!eq(draftFilters.markets, activeFilters.markets)) n++;
    if (draftFilters.cost !== activeFilters.cost) n++;
    if (draftFilters.dateType !== activeFilters.dateType) n++;
    if (draftFilters.viewMode !== activeFilters.viewMode) n++;
    if (!eq(draftFilters.selectedItems, activeFilters.selectedItems)) n++;
    return n;
  }, [draftFilters, activeFilters]);

  // Revert the draft back to what's currently applied. Used by the "Descartar"
  // action in the pending-changes banner.
  const discardDraftChanges = () => {
    setDraftFilters(activeFilters);
  };

  // Human-readable summary of which sidebar filters are currently narrowing
  // the dataset. Drives the Análisis Comparativo disclaimer. Only includes
  // filters that actually affect this section's calculations (date_type is
  // omitted because rankings are season-scoped).
  const activeFilterChips = useMemo(() => {
    const chips: { label: string; value: string }[] = [];

    if (sinisterFilter === 'sinister') {
      chips.push({ label: t('Estado'), value: t('Solo siniestradas') });
    } else if (sinisterFilter === 'non_sinister') {
      chips.push({ label: t('Estado'), value: t('No siniestradas') });
    }

    if (!receiverFilter.includes('all')) {
      const value =
        receiverFilter.length === 0
          ? t('Ninguno')
          : receiverFilter.length <= 2
            ? receiverFilter.join(', ')
            : `${receiverFilter.length} ${t('seleccionados')}`;
      chips.push({ label: t('Clientes'), value });
    }

    if (!canonicalMarketFilter.includes('all')) {
      const marketValues = canonicalMarketFilter.map((value) =>
        value === MISSING_MARKET ? t(MISSING_MARKET) : value
      );
      const value =
        canonicalMarketFilter.length === 0
          ? t('Ninguno')
          : canonicalMarketFilter.length <= 2
            ? marketValues.join(', ')
            : `${canonicalMarketFilter.length} ${t('seleccionados')}`;
      chips.push({ label: t('Mercados'), value });
    }

    if (costFilter === 'complete_only') {
      chips.push({ label: t('Costos'), value: t('Solo completos') });
    }

    if (!selectedItems.includes('all') && selectedItems.length > 0) {
      const label = viewMode === 'ship' ? t('Naves') : t('Semanas');
      const value =
        selectedItems.length === 1
          ? `1 ${t('seleccionado')}`
          : `${selectedItems.length} ${t('seleccionados')}`;
      chips.push({ label, value });
    }

    return chips;
  }, [
    sinisterFilter,
    receiverFilter,
    canonicalMarketFilter,
    costFilter,
    selectedItems,
    viewMode,
    t,
  ]);

  const openFilterModal = () => {
    setDraftFilters(activeFilters);
    setIsFilterOpen(true);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
  };

  const closeFilterModal = () => {
    setIsFilterOpen(false);
  };

  const applyFilters = () => {
    // Collapse "all selected" multi-selects to the ['all'] sentinel so the URL
    // stays clean and the downstream "no filter" path is taken. Keep empty []
    // as-is — it means "user explicitly deselected everything".
    const normalizedDraftMarkets = normalizeMarketFilterValues(
      draftFilters.markets
    );
    const normalized: ActiveFilters = {
      ...draftFilters,
      receivers:
        draftFilters.receivers.length === allReceivers.length &&
        allReceivers.length > 0
          ? ['all']
          : draftFilters.receivers,
      markets:
        normalizedDraftMarkets.includes('all')
          ? ['all']
          : normalizedDraftMarkets.length === allMarkets.length &&
              allMarkets.length > 0
          ? ['all']
          : normalizedDraftMarkets,
    };
    setActiveFilters(normalized);
    setDraftFilters(normalized);
    writeFiltersToURL(normalized);
    // Only auto-close on mobile, where the sidebar is a true overlay modal.
    // On desktop the sidebar lives alongside the page — keep it open so the
    // user can keep iterating on filters without reopening.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    ) {
      setIsFilterOpen(false);
    }
  };

  const resetDraftFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
  };

  const setDraft = <K extends keyof ActiveFilters>(
    key: K,
    value: ActiveFilters[K]
  ) => {
    setDraftFilters((d) => ({ ...d, [key]: value }));
  };

  const filteredSettlements = useMemo(() => {
    let filtered = settlements;

    if (costFilter === 'complete_only') {
      filtered = settlements.filter(
        (s) =>
          s.total_commission_local !== null &&
          s.total_commission_local > 0 &&
          s.total_tax_local !== null &&
          s.total_tax_local > 0 &&
          ((s.total_other_costs_local !== null &&
            s.total_other_costs_local > 0) ||
            ((s.total_repacking_costs_local || 0) > 0)) &&
          s.total_revenue_local !== null &&
          s.total_revenue_local > 0
      );
    }

    if (sinisterFilter !== 'all') {
      filtered = filtered.filter((settlement) =>
        sinisterFilter === 'sinister'
          ? settlement.is_sinister
          : !settlement.is_sinister
      );
    }

    // ['all'] = no narrowing. [] = explicit deselect (show nothing).
    // Subset = filter to those values.
    if (!receiverFilter.includes('all')) {
      if (receiverFilter.length === 0) {
        filtered = [];
      } else {
        filtered = filtered.filter((settlement) =>
          receiverFilter.includes(settlement.receiver)
        );
      }
    }

    if (!canonicalMarketFilter.includes('all')) {
      if (canonicalMarketFilter.length === 0) {
        filtered = [];
      } else {
        filtered = filtered.filter((settlement) => {
          const settlementMarket = settlement.market?.name || MISSING_MARKET;
          return canonicalMarketFilter.includes(settlementMarket);
        });
      }
    }

    if (selectedItems.length === 0 || selectedItems.includes('all')) {
      return filtered;
    }

    // When specific ships/weeks are selected, restrict `filtered` to only
    // settlements that belong to those groups (instead of bypassing earlier filters)
    const groupedData =
      viewMode === 'ship'
        ? groupedSettlements.byShip
        : groupedSettlements.byWeek;

    const selectedSettlementIds = new Set(
      selectedItems.flatMap((key) => (groupedData[key] || []).map((s) => s.id))
    );

    return filtered.filter((s) => selectedSettlementIds.has(s.id));
  }, [
    settlements,
    groupedSettlements,
    viewMode,
    selectedItems,
    sinisterFilter,
    receiverFilter,
    canonicalMarketFilter,
    costFilter,
  ]);

  const hasAnyExtraRevenue = useMemo(
    () =>
      filteredSettlements.some((s) => (s.total_other_revenue_local || 0) !== 0),
    [filteredSettlements]
  );

  const revenueByDetailId = useMemo(() => {
    const map = new Map<number, number>();
    if (!showWithRevenues || !hasAnyExtraRevenue) return map;
    for (const s of filteredSettlements) {
      for (const d of s.settlement_details) {
        const revUsd = (d.settlement_detail_revenues || []).reduce(
          (sum: number, r: any) => sum + (r.amount_usd || 0),
          0
        );
        if (d.id && revUsd !== 0) {
          map.set(d.id, revUsd);
        }
      }
    }
    return map;
  }, [showWithRevenues, hasAnyExtraRevenue, filteredSettlements]);

  const { totalFob, totalKgs } = useMemo(() => {
    return filteredSettlements.reduce(
      (acc, settlement) => {
        const settlementFob = calculateFOBSettlement(settlement);
        const settlementKgs = settlement.settlement_details.reduce(
          (detailAcc, detail) => detailAcc + (detail.kilos || 0),
          0
        );
        return {
          totalFob: acc.totalFob + settlementFob,
          totalKgs: acc.totalKgs + settlementKgs,
        };
      },
      { totalFob: 0, totalKgs: 0 }
    );
  }, [filteredSettlements]);

  const totalRevenueUsd = useMemo(() => {
    let total = 0;
    for (const amount of revenueByDetailId.values()) {
      total += amount;
    }
    return total;
  }, [revenueByDetailId]);

  const adjustedTotalFob = totalFob + totalRevenueUsd;

  const adjustedFilteredSettlements = useMemo(() => {
    if (!showWithRevenues || revenueByDetailId.size === 0)
      return filteredSettlements;
    return filteredSettlements.map((s) => ({
      ...s,
      settlement_details: s.settlement_details.map((d) => {
        const revUsd = revenueByDetailId.get(d.id) || 0;
        if (revUsd === 0) return d;
        return {
          ...d,
          usd_total_fob: (d.usd_total_fob || 0) + revUsd,
        };
      }),
    }));
  }, [filteredSettlements, showWithRevenues, revenueByDetailId]);

  const handleClearChartFilters = useCallback(() => {
    const details = adjustedFilteredSettlements.flatMap(
      (s) => s.settlement_details || []
    );
    const allVarieties = [
      ...new Set(details.map((d) => d.variety?.toUpperCase()).filter(Boolean)),
    ] as string[];
    const allCalibers = [
      ...new Set(details.map((d) => d.caliber).filter(Boolean)),
    ] as string[];
    const allFormats = [
      ...new Set(details.map((d) => d.format).filter(Boolean)),
    ] as string[];
    const allLabels = [
      ...new Set(details.map((d) => d.label || t('Sin Etiqueta'))),
    ] as string[];
    const allMarkets = [
      ...new Set(
        adjustedFilteredSettlements.map(
          (s) => s.market?.name || t('Sin Mercado')
        )
      ),
    ] as string[];
    const allReceivers = [
      ...new Set(adjustedFilteredSettlements.map((s) => s.receiver)),
    ] as string[];
    const allFleets = [
      ...new Set(
        adjustedFilteredSettlements.map((s) => {
          const t = s.ship_trip?.ship?.type;
          return t === 'A' ? 'AIR' : t === 'T' ? 'GROUND' : 'OCEAN';
        })
      ),
    ] as string[];
    const allCountries = [
      ...new Set(
        adjustedFilteredSettlements.map(
          (s) => s.market?.country || t('Sin País')
        )
      ),
    ] as string[];
    const allBusinessTypes = [
      ...new Set(
        adjustedFilteredSettlements.map(
          (s) => s.business_type || MISSING_BUSINESS_TYPE
        )
      ),
    ] as string[];
    const allColors = [
      ...new Set(details.map((d) => d.color || 'Sin Color')),
    ] as string[];

    setChartFilters((prev) => ({
      ...prev,
      variety: allVarieties,
      caliber: allCalibers,
      format: allFormats,
      color: allColors,
      market: allMarkets,
      label: allLabels,
      receiver: allReceivers,
      fleet: allFleets,
      country: allCountries,
      business_type: allBusinessTypes,
    }));
  }, [adjustedFilteredSettlements]);

  // When the sidebar filters change, the universe of values available to the
  // chart chip filters changes too. Reset the chip selections to the new
  // universe so stale selections (e.g. all-clients from before narrowing) don't
  // linger in the FOB filter chips. Skip initial mount to preserve deep-linked
  // state.
  const chipResetMount = useRef(true);
  useEffect(() => {
    if (chipResetMount.current) {
      chipResetMount.current = false;
      return;
    }
    handleClearChartFilters();
  }, [activeFilters, handleClearChartFilters]);

  // Scaled settlements for FOB-by-caliber-color and FOB-by-week-variety tables,
  // which display values in "per-box equivalent" units. Memoized so the
  // downstream React.memo'd tables don't re-render when unrelated state changes.
  const fobScaledSettlements = useMemo(() => {
    const scale =
      kgOrBox === 'kg' ? 1 : selectedSpecies?.default_unit_price || 5;
    if (scale === 1) return adjustedFilteredSettlements;
    return adjustedFilteredSettlements.map((s) => ({
      ...s,
      settlement_details: s.settlement_details.map((sd) => ({
        ...sd,
        usd_total_fob: sd.usd_total_fob * scale,
      })),
    }));
  }, [
    adjustedFilteredSettlements,
    kgOrBox,
    selectedSpecies?.default_unit_price,
  ]);

  const chartFilteredSettlements = useMemo(() => {
    const filtersWithDateType = { ...chartFilters, dateType };
    return adjustedFilteredSettlements
      .map((s) => ({
        ...s,
        settlement_details: s.settlement_details.filter((d) =>
          filterSettlementDetails(d, s, filtersWithDateType)
        ),
      }))
      .filter((s) => s.settlement_details.length > 0);
  }, [adjustedFilteredSettlements, chartFilters, dateType]);

  // Are ANY client-side-only filters narrowing data beyond what the sidebar
  // distribution RPC knows about? Two cases:
  //   (a) In-chart chip filters (variety/caliber/color/...) — detected by
  //       comparing detail counts before vs after chip filtering.
  //   (b) Ship/week selection in the sidebar — the RPC does not accept these
  //       yet, so we fall back to the legacy client-side aggregation.
  const shouldFallbackToLegacyAgg = useMemo(() => {
    const selectedItemsNarrowing = !selectedItems.includes('all');
    if (selectedItemsNarrowing) return true;
    const adjDetails = adjustedFilteredSettlements.reduce(
      (n, s) => n + (s.settlement_details?.length || 0),
      0
    );
    const chartDetails = chartFilteredSettlements.reduce(
      (n, s) => n + (s.settlement_details?.length || 0),
      0
    );
    return chartDetails < adjDetails;
  }, [adjustedFilteredSettlements, chartFilteredSettlements, selectedItems]);
  // Kept under the old name for downstream readability.
  const chipFiltersNarrowing = shouldFallbackToLegacyAgg;

  const distributionChartData = useMemo(() => {
    const boxSize = selectedSpecies?.default_unit_price || 5;
    const mult = kgOrBox === 'kg' ? 1 : boxSize;

    const groupByDetail = (keyFn: (d: any) => string | null) => {
      const groups: Record<string, { fob: number; kilos: number }> = {};
      chartFilteredSettlements.forEach((s) => {
        s.settlement_details?.forEach((d) => {
          const key = keyFn(d);
          if (!key) return;
          if (!groups[key]) groups[key] = { fob: 0, kilos: 0 };
          groups[key].fob += d.usd_total_fob;
          groups[key].kilos += d.kilos;
        });
      });
      return Object.entries(groups).map(([label, g]) => ({
        label,
        fobPerUnit: g.kilos > 0 ? (g.fob / g.kilos) * mult : 0,
        volume: kgOrBox === 'kg' ? g.kilos : g.kilos / boxSize,
      }));
    };

    const groupBySettlement = (keyFn: (s: Settlement) => string) => {
      const groups: Record<string, { fob: number; kilos: number }> = {};
      chartFilteredSettlements.forEach((s) => {
        const key = keyFn(s);
        if (!groups[key]) groups[key] = { fob: 0, kilos: 0 };
        groups[key].fob += s.settlement_details.reduce(
          (sum, d) => sum + (d.usd_total_fob || 0),
          0
        );
        groups[key].kilos += s.settlement_details.reduce(
          (sum, d) => sum + (d.kilos || 0),
          0
        );
      });
      return Object.entries(groups).map(([label, g]) => ({
        label,
        fobPerUnit: g.kilos > 0 ? (g.fob / g.kilos) * mult : 0,
        volume: kgOrBox === 'kg' ? g.kilos : g.kilos / boxSize,
      }));
    };

    return {
      client: groupBySettlement((s) => s.receiver),
      variety: groupByDetail((d) => d.variety),
      caliber: groupByDetail((d) => d.caliber || null),
      format: groupByDetail((d) => d.format || null),
      color: groupByDetail((d) => d.color || null),
      market: groupBySettlement((s) => s.market?.name || t('Sin Mercado')),
      label: groupByDetail((d) => d.label || t('Sin Etiqueta')),
      fleet: groupBySettlement((s) => {
        const type = s.ship_trip?.ship?.type;
        if (type === 'A') return t('Aéreo');
        if (type === 'T') return t('Terrestre');
        return t('Marítimo');
      }),
      country: groupBySettlement((s) => s.market?.country || t('Sin País')),
      business_type: groupBySettlement(
        (s) => s.business_type || t('Sin Tipo de Negocio')
      ),
    };
  }, [chartFilteredSettlements, kgOrBox, selectedSpecies?.default_unit_price]);

  // Localize the nullable "key" coming out of the distribution RPC. The
  // server leaves null for "empty" categories so the label stays i18n-aware.
  const localizeDistributionKey = useCallback(
    (grouping: string, key: string | null): string => {
      if (grouping === 'market') return key ?? t('Sin Mercado');
      if (grouping === 'country') return key ?? t('Sin País');
      if (grouping === 'label') return key ?? t('Sin Etiqueta');
      if (grouping === 'business_type') return key ?? t('Sin Tipo de Negocio');
      if (grouping === 'fleet') {
        if (key === 'AIR') return t('Aéreo');
        if (key === 'GROUND') return t('Terrestre');
        if (key === 'OCEAN') return t('Marítimo');
        return key ?? '';
      }
      return key ?? '';
    },
    [t]
  );

  // DistributionBarChart data derived from the server-aggregated RPC result.
  // Falls back to [] when the RPC hasn't landed yet; the UI continues to work
  // with the legacy client-side `distributionChartData` in that window.
  const distributionBarChartDataFromRPC = useMemo(() => {
    if (!distributionData) return null;
    const boxSize = selectedSpecies?.default_unit_price || 5;
    const mult = kgOrBox === 'kg' ? 1 : boxSize;

    const transform = (
      grouping: keyof SettlementDistributionResult['groupings']
    ) =>
      distributionData.groupings[grouping].map((row) => {
        const fob =
          row.total_fob + (showWithRevenues ? row.total_extra_revenue_usd : 0);
        return {
          label: localizeDistributionKey(grouping, row.key),
          fobPerUnit: row.total_kilos > 0 ? (fob / row.total_kilos) * mult : 0,
          volume:
            kgOrBox === 'kg' ? row.total_kilos : row.total_kilos / boxSize,
        };
      });

    return {
      client: transform('client'),
      variety: transform('variety'),
      caliber: transform('caliber'),
      format: transform('format'),
      color: transform('color'),
      label: transform('label'),
      market: transform('market'),
      country: transform('country'),
      fleet: transform('fleet'),
      business_type: transform('business_type'),
    };
  }, [
    distributionData,
    kgOrBox,
    selectedSpecies?.default_unit_price,
    showWithRevenues,
    localizeDistributionKey,
  ]);

  // Synthetic Settlement[] derived from the distribution RPC's filter_universe.
  // Used as a stand-in for chip filter components while the heavier monolithic
  // RPC is still loading — they only inspect a few fields to derive option
  // lists, so a minimum-viable shape is enough to render correctly. Once the
  // monolithic data lands, the real `adjustedFilteredSettlements` takes over.
  const filterUniverseSettlements = useMemo<Settlement[] | null>(() => {
    if (!distributionData) return null;
    const u = distributionData.filter_universe;
    const g = distributionData.groupings;
    const receivers =
      u?.receivers ?? (g.client.map((r) => r.key).filter(Boolean) as string[]);
    const markets =
      u?.markets ?? (g.market.map((r) => r.key).filter(Boolean) as string[]);
    const countries =
      u?.countries ?? (g.country.map((r) => r.key).filter(Boolean) as string[]);
    const varieties =
      u?.varieties ?? (g.variety.map((r) => r.key).filter(Boolean) as string[]);
    const calibers =
      u?.calibers ?? (g.caliber.map((r) => r.key).filter(Boolean) as string[]);
    const rawColors =
      u?.colors ?? (g.color.map((r) => r.key).filter(Boolean) as string[]);
    const colorKilos = g.color.reduce(
      (sum, row) => sum + (row.total_kilos || 0),
      0
    );
    const hasUncoloredRows =
      (distributionData.grand_totals?.total_kilos || 0) > colorKilos;
    const colors =
      hasUncoloredRows && !rawColors.includes('Sin Color')
        ? [...rawColors, 'Sin Color']
        : rawColors;
    const formats =
      u?.formats ?? (g.format.map((r) => r.key).filter(Boolean) as string[]);
    const labels =
      u?.labels ?? (g.label.map((r) => r.key).filter(Boolean) as string[]);
    const fleets =
      u?.fleets ?? (g.fleet.map((r) => r.key).filter(Boolean) as string[]);
    const rawBusinessTypes =
      u?.business_types ??
      (g.business_type.map((r) => r.key).filter(Boolean) as string[]);
    const hasMissingBusinessType = g.business_type.some(
      (row) => row.key == null
    );
    const businessTypes =
      hasMissingBusinessType &&
      !rawBusinessTypes.includes(MISSING_BUSINESS_TYPE)
        ? [...rawBusinessTypes, MISSING_BUSINESS_TYPE]
        : rawBusinessTypes;

    const len = Math.max(
      receivers.length,
      markets.length,
      countries.length,
      varieties.length,
      calibers.length,
      colors.length,
      formats.length,
      labels.length,
      fleets.length,
      businessTypes.length
    );
    if (len === 0) return [];

    const synth: Settlement[] = [];
    for (let i = 0; i < len; i++) {
      const fleetKey = fleets[i];
      synth.push({
        id: -1 - i,
        receiver: receivers[i] ?? '',
        business_type: businessTypes[i],
        market: {
          name: markets[i] ?? 'Sin Mercado',
          country: countries[i] ?? 'Sin País',
        } as any,
        ship_trip: fleetKey
          ? ({
              ship: {
                type:
                  fleetKey === 'AIR' ? 'A' : fleetKey === 'GROUND' ? 'T' : 'O',
              },
            } as any)
          : undefined,
        settlement_details: [
          {
            variety: varieties[i],
            caliber: calibers[i],
            format: formats[i],
            color: colors[i],
            label: labels[i],
          } as any,
        ],
      } as Settlement);
    }
    return synth;
  }, [distributionData]);

  // Settlements to feed the chip filter section: prefer real data once the
  // monolithic RPC has landed; fall back to filter_universe-derived synthetics
  // so the filters can render at ~3s alongside Acumulado.
  const filtersInputSettlements = useMemo(() => {
    if (adjustedFilteredSettlements.length > 0)
      return adjustedFilteredSettlements;
    return filterUniverseSettlements ?? [];
  }, [adjustedFilteredSettlements, filterUniverseSettlements]);

  // Aggregated rows for SettlementsVarietyFOBTable, transformed per grouping.
  const varietyTableAggregated = useMemo(() => {
    if (!distributionData) return null;
    if (distributionTab === 'client') return null;
    const grouping =
      distributionTab as keyof SettlementDistributionResult['groupings'];
    return distributionData.groupings[grouping].map((row) => ({
      key: localizeDistributionKey(grouping, row.key),
      total_fob: row.total_fob,
      total_kilos: row.total_kilos,
      total_extra_revenue_usd: row.total_extra_revenue_usd,
    }));
  }, [distributionData, distributionTab, localizeDistributionKey]);

  useEffect(() => {
    const boxSize = selectedSpecies?.default_unit_price || 5;
    setMultiplier(kgOrBox === 'kg' ? 1 : boxSize);
  }, [kgOrBox, selectedSpecies?.default_unit_price]);

  useEffect(() => {
    if (!selectedSpecies || !userProfile?.exporter_id) return;
    let cancelled = false;

    const fetchGlobalFobBenchmark = async () => {
      setGlobalFobBenchmarkLoading(true);
      setGlobalFobBenchmark(null);
      const result = await (
        useDetailEconomicsV2
          ? getExporterGlobalFobPercentileV2
          : getExporterGlobalFobPercentile
      )(
        +userProfile.exporter_id!,
        selectedSeason,
        selectedSpecies.id!,
        'by_exporter',
        'all'
      );
      if (cancelled) return;
      setGlobalFobBenchmark(result);
      setGlobalFobBenchmarkLoading(false);
    };

    fetchGlobalFobBenchmark();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSeason,
    selectedSpecies,
    useDetailEconomicsV2,
    userProfile?.exporter_id,
  ]);

  useEffect(() => {
    if (!selectedSpecies || !userProfile?.exporter_id) return;
    let cancelled = false;

    const fetchCounts = async () => {
      setShipmentSettlementCountsLoading(true);
      setShipmentSettlementCounts(null);
      try {
        const result = await getShipmentSettlementCounts(
          +userProfile.exporter_id!,
          selectedSeason,
          selectedSpecies.id!
        );
        if (cancelled) return;
        setShipmentSettlementCounts(result);
      } finally {
        if (!cancelled) {
          setShipmentSettlementCountsLoading(false);
        }
      }
    };

    fetchCounts();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSeason,
    selectedSpecies,
    session,
    userProfile?.exporter?.hasDemo,
    userProfile?.exporter_id,
  ]);

  useEffect(() => {
    const previousSeason = getPreviousSeasonName(selectedSeason);
    if (!previousSeason || !selectedSpecies || !userProfile?.exporter_id)
      return;
    let cancelled = false;

    const fetchPreviousSeasonDistribution = async () => {
      setPreviousSeasonKpisLoading(true);
      setPreviousSeasonDistribution(null);
      const result = await (
        useDetailEconomicsV2
          ? getSettlementDistributionV2
          : getSettlementDistribution
      )(
        +userProfile.exporter_id!,
        previousSeason,
        selectedSpecies.id!,
        {
          sinister: sinisterFilter,
          receivers: receiverFilter.includes('all')
            ? undefined
            : receiverFilter,
          markets: canonicalMarketFilter.includes('all')
            ? undefined
            : canonicalMarketFilter,
          week_keys:
            viewMode === 'week'
              ? getPreviousSeasonWeekKeys(selectedItems, selectedSeason)
              : undefined,
          cost_methodology: costFilter,
          date_type: dateType,
        }
      );
      if (cancelled) return;
      setPreviousSeasonDistribution(result);
      setPreviousSeasonKpisLoading(false);
    };

    fetchPreviousSeasonDistribution();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSeason,
    selectedSpecies,
    sinisterFilter,
    receiverFilter,
    canonicalMarketFilter,
    selectedItems,
    viewMode,
    costFilter,
    dateType,
    useDetailEconomicsV2,
    userProfile?.exporter_id,
  ]);

  useEffect(() => {
    let cancelled = false;

    const fetchKpis = async () => {
      setKpisLoading(true);
      setInitialKpis(null);
      const kpis = await (
        useDetailEconomicsV2
          ? getSettlementSummaryKPIsV2
          : getSettlementSummaryKPIs
      )(
        +userProfile?.exporter_id!,
        selectedSeason,
        selectedSpecies?.id!
      );
      if (cancelled) return;
      if (kpis) {
        setInitialKpis(kpis);
        if (kpis.has_missing_data) setMissingDataAlert(true);
      }
      setKpisLoading(false);
    };

    const fetchSettlements = async () => {
      setLoading(true);
      setSettlements([]);
      setOriginalSettlements([]);
      setGroupedSettlements({ byShip: {}, byWeek: {} });

      const result = await (
        useDetailEconomicsV2
          ? getSettlementSummaryRPCV2
          : getSettlementSummaryRPC
      )(
        +userProfile?.exporter_id!,
        selectedSeason,
        selectedSpecies?.id!,
        'all',
        showWithRevenues
      );

      if (cancelled) return;

      if (!result || !result.settlements) {
        setLoading(false);
        return;
      }

      if (result.kpis.has_missing_data) {
        setMissingDataAlert(true);
      }

      const processed =
        (result.settlements.map((s) => ({
          ...s,
          settlement_details: s.settlement_details.map((sd) => ({
            ...sd,
            format: `${sd.format}-${sd.unit_weight}KG`,
          })),
        })) as Settlement[]) || [];

      if (cancelled) return;

      setSettlements(processed);
      setOriginalSettlements(processed);
      setSettlementsForCosts(processed);
      const byShip = groupSettlementsByShip(processed);
      const byWeek = groupSettlementsByWeek(processed);

      setGroupedSettlements({ byShip, byWeek });

      setLoading(false);
    };
    if (selectedSpecies) {
      fetchKpis();
      fetchSettlements();
    }

    return () => {
      cancelled = true;
    };
  }, [
    selectedSeason,
    selectedSpecies,
    showWithRevenues,
    useDetailEconomicsV2,
    userProfile?.exporter_id,
  ]);

  // Fetch the aggregates-only distribution RPC on page load and on every
  // Apply of the sidebar filters. Runs in parallel with the legacy monolithic
  // RPC above (which still drives the below-the-fold sections).
  useEffect(() => {
    if (!selectedSpecies) return;
    let cancelled = false;

    const fetchDistribution = async () => {
      setDistributionLoading(true);
      const result = await (
        useDetailEconomicsV2
          ? getSettlementDistributionV2
          : getSettlementDistribution
      )(
        +userProfile?.exporter_id!,
        selectedSeason,
        selectedSpecies.id!,
        {
          sinister: sinisterFilter,
          receivers: receiverFilter.includes('all')
            ? undefined
            : receiverFilter,
          markets: canonicalMarketFilter.includes('all')
            ? undefined
            : canonicalMarketFilter,
          cost_methodology: costFilter,
          date_type: dateType,
        }
      );
      if (cancelled) return;
      setDistributionData(result);
      setDistributionLoading(false);
    };

    fetchDistribution();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSeason,
    selectedSpecies,
    sinisterFilter,
    receiverFilter,
    canonicalMarketFilter,
    costFilter,
    dateType,
    useDetailEconomicsV2,
    userProfile?.exporter_id,
  ]);

  // Re-fetch with revenues when toggle is enabled (only fetches detail revenues, not full reload)
  useEffect(() => {
    if (
      !showWithRevenues ||
      !selectedSpecies ||
      settlements.length === 0 ||
      !hasAnyExtraRevenue
    )
      return;

    const hasLoadedRevenueDetails = settlements.some((s) =>
      s.settlement_details.some(
        (d) => (d.settlement_detail_revenues || []).length > 0
      )
    );
    if (hasLoadedRevenueDetails) return;

    let cancelled = false;

    const fetchWithRevenues = async () => {
      setRevenuesLoading(true);
      const result = await (
        useDetailEconomicsV2
          ? getSettlementSummaryRPCV2
          : getSettlementSummaryRPC
      )(
        +userProfile?.exporter_id!,
        selectedSeason,
        selectedSpecies?.id!,
        'all',
        true // include revenues
      );

      if (cancelled || !result?.settlements) {
        setRevenuesLoading(false);
        return;
      }

      const processed =
        (result.settlements.map((s) => ({
          ...s,
          settlement_details: s.settlement_details.map((sd) => ({
            ...sd,
            format: `${sd.format}-${sd.unit_weight}KG`,
          })),
        })) as Settlement[]) || [];

      setSettlements(processed);
      setOriginalSettlements(processed);
      setSettlementsForCosts(processed);
      setRevenuesLoading(false);
    };

    fetchWithRevenues();

    return () => {
      cancelled = true;
    };
  }, [
    showWithRevenues,
    selectedSpecies,
    settlements,
    hasAnyExtraRevenue,
    selectedSeason,
    useDetailEconomicsV2,
    userProfile?.exporter_id,
  ]);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-summary');
  }, []);

  // Sort + describe ship/week groups for the modal's multi-select.
  // Previously computed inline on every render; memoized on the inputs that
  // actually change its output.
  const shipWeekItems = useMemo(() => {
    const mode = draftFilters.viewMode;
    const groupedData =
      mode === 'ship' ? groupedSettlements.byShip : groupedSettlements.byWeek;

    const sorted = Object.entries(groupedData)
      .sort(([keyA], [keyB]) => {
        if (mode === 'week') {
          return sortWeekKeys(keyB, keyA);
        }
        if (keyA.startsWith('AIR-') && keyB.startsWith('AIR-')) {
          const [, yearA, weekA] = keyA.split('-');
          const [, yearB, weekB] = keyB.split('-');
          return sortWeekKeys(`${yearB}-${weekB}`, `${yearA}-${weekA}`);
        }
        if (keyA.startsWith('AIR-')) return -1;
        if (keyB.startsWith('AIR-')) return 1;
        if (keyA.startsWith('SHIP-') && keyB.startsWith('SHIP-')) {
          const dateA = new Date(keyA.split('-').slice(-1)[0]);
          const dateB = new Date(keyB.split('-').slice(-1)[0]);
          return dateB.getTime() - dateA.getTime();
        }
        if (keyA.startsWith('SHIP-')) return -1;
        if (keyB.startsWith('SHIP-')) return 1;
        if (keyA.startsWith('GROUND-') && keyB.startsWith('GROUND-')) {
          const [, yearA, weekA] = keyA.split('-');
          const [, yearB, weekB] = keyB.split('-');
          return sortWeekKeys(`${yearB}-${weekB}`, `${yearA}-${weekA}`);
        }
        if (keyA.startsWith('GROUND-')) return 1;
        if (keyB.startsWith('GROUND-')) return -1;
        return keyA.localeCompare(keyB);
      })
      .map(([key, groupSettlements]) => {
        const label =
          mode === 'ship'
            ? formatXAxisLabel(key, 'ships')
            : formatXAxisLabel(key, 'weeks');

        let description = '';
        if (
          mode === 'ship' &&
          key.startsWith('SHIP-') &&
          groupSettlements.length > 0
        ) {
          const first = groupSettlements[0];
          const market = first.market?.name || '';
          const eta = first.ship_trip?.eta
            ? new Date(first.ship_trip.eta).toLocaleDateString('es-CL', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : '';
          const parts = [market, eta ? `ETA ${eta}` : ''].filter(Boolean);
          description = parts.join(' · ');
        }

        return { key, label, description };
      });

    const search = shipWeekSearch.toLowerCase().trim();
    const filtered = search
      ? sorted.filter(
          (item) =>
            item.label.toLowerCase().includes(search) ||
            item.description.toLowerCase().includes(search)
        )
      : sorted;

    return [
      {
        key: 'all',
        label:
          mode === 'ship'
            ? t('Todas las naves/envíos')
            : t('Todas las semanas'),
        description: '',
      },
      ...filtered,
    ];
  }, [draftFilters.viewMode, groupedSettlements, shipWeekSearch, t]);

  // Reusable section skeleton
  const SectionSkeleton = ({ rows = 4 }: { rows?: number }) => (
    <Card className='w-full p-2'>
      <CardBody className='p-4 space-y-2'>
        <Skeleton className='h-6 w-full rounded-lg' />
        {[...Array(rows)].map((_, i) => (
          <Skeleton key={i} className='h-8 w-full rounded-lg' />
        ))}
      </CardBody>
    </Card>
  );

  const KPICardsSkeleton = () => (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
      {[...Array(2)].map((_, i) => (
        <Card key={i} className='w-full p-2'>
          <CardBody className='p-5 space-y-3'>
            <Skeleton className='h-4 w-1/3 rounded-lg' />
            <Skeleton className='h-9 w-1/2 rounded-lg' />
            <Skeleton className='h-4 w-2/3 rounded-lg' />
          </CardBody>
        </Card>
      ))}
    </div>
  );

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <ModalSlideFilter isOpen={isFilterOpen} onClose={closeFilterModal}>
        <div className='bg-background h-full flex flex-col'>
          {hasDraftChanges && (
            <Alert variant='warning' showIcon className='mx-4 mt-4'>
              <p>
                {draftChangeCount === 1
                  ? t('1 cambio sin aplicar')
                  : `${draftChangeCount} ${t('cambios sin aplicar')}`}
              </p>
              <div className='flex gap-2 mt-2.5'>
                <Button
                  variant='light'
                  size='sm'
                  onPress={discardDraftChanges}
                  className='flex-shrink-0'
                >
                  {t('Descartar')}
                </Button>
                <Button
                  color='primary'
                  size='sm'
                  className='flex-1'
                  onPress={applyFilters}
                >
                  {t('Aplicar')}
                </Button>
              </div>
            </Alert>
          )}
          <div className='flex-1 overflow-y-auto p-4 pt-6'>
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <p className='text-sm text-default-500'>{t('Filtros')}</p>
                <button
                  type='button'
                  onClick={resetDraftFilters}
                  className='text-xs text-default-500 hover:text-default-700 dark:hover:text-default-300 underline-offset-2 hover:underline'
                >
                  {t('Limpiar todo')}
                </button>
              </div>
              {/* ── Primary filters ── */}
              <SeasonFilter />
              <ReceiverFilter
                data={settlements}
                selectedReceiver={
                  draftFilters.receivers.includes('all')
                    ? allReceivers
                    : draftFilters.receivers
                }
                onSelectionChange={(value) => {
                  const list = Array.from(value);
                  setDraft(
                    'receivers',
                    list.length === allReceivers.length &&
                      allReceivers.length > 0
                      ? ['all']
                      : list
                  );
                }}
                selectionMode='multiple'
              />
              <MarketFilter
                data={settlements}
                selectedMarket={
                  draftFilters.markets.includes('all')
                    ? allMarkets
                    : normalizeMarketFilterValues(draftFilters.markets)
                }
                onSelectionChange={(value) => {
                  const list = normalizeMarketFilterValues(Array.from(value));
                  setDraft(
                    'markets',
                    list.length === allMarkets.length && allMarkets.length > 0
                      ? ['all']
                      : list
                  );
                }}
                selectionMode='multiple'
              />

              {/* ── Filtrar adicionalmente por ── */}
              {/* <div className='pt-2 border-t border-default-100 dark:border-default-50' /> */}
              {/* <p className='text-sm font-medium text-default-600 dark:text-default-400'>
                {t('Filtrar adicionalmente por')}
              </p> */}
              <Tabs
                aria-label={t('Filtrar por')}
                selectedKey={draftFilters.viewMode}
                onSelectionChange={(key) => {
                  const newMode = key as ViewModeChoice;
                  setDraftFilters((d) => ({
                    ...d,
                    viewMode: newMode,
                    selectedItems: ['all'],
                  }));
                }}
                size='sm'
                fullWidth
              >
                <Tab key='ship' title={t('Nave')} />
                <Tab key='week' title={t('Semana')} />
              </Tabs>
              <Select
                label={
                  draftFilters.viewMode === 'ship'
                    ? t('Naves/Envíos')
                    : t('Semana de Venta')
                }
                selectionMode='multiple'
                selectedKeys={draftFilters.selectedItems}
                onSelectionChange={(keys) => {
                  const selectedKeys = Array.from(keys as Set<string>);
                  const currentItems = draftFilters.selectedItems;
                  let next: string[];
                  if (selectedKeys.length === 0) {
                    next = ['all'];
                  } else if (
                    selectedKeys.includes('all') &&
                    !currentItems.includes('all')
                  ) {
                    next = ['all'];
                  } else if (
                    currentItems.includes('all') &&
                    selectedKeys.length > 1
                  ) {
                    next = selectedKeys.filter((key) => key !== 'all');
                  } else {
                    next = selectedKeys;
                  }
                  setDraft('selectedItems', next);
                }}
                className='w-full'
                items={shipWeekItems}
                listboxProps={{
                  topContent: (
                    <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
                      <Input
                        size='sm'
                        placeholder={
                          draftFilters.viewMode === 'ship'
                            ? t('Buscar nave...')
                            : t('Buscar semana...')
                        }
                        value={shipWeekSearch}
                        onValueChange={setShipWeekSearch}
                        isClearable
                        onClear={() => setShipWeekSearch('')}
                      />
                    </div>
                  ),
                }}
              >
                {(item) => (
                  <SelectItem key={item.key} textValue={item.label}>
                    <div>
                      <div>{item.label}</div>
                      {item.description && (
                        <div className='text-xs text-default-400'>
                          {item.description}
                        </div>
                      )}
                    </div>
                  </SelectItem>
                )}
              </Select>
              <Select
                label={t('Estado Siniestro')}
                selectedKeys={[draftFilters.sinister]}
                onChange={(e) =>
                  setDraft('sinister', e.target.value as SinisterChoice)
                }
                className='w-full'
              >
                <SelectItem key='all' value='all'>
                  {t('Todas las liquidaciones')}
                </SelectItem>
                <SelectItem key='sinister' value='sinister'>
                  {t('Solo siniestradas')}
                </SelectItem>
                <SelectItem key='non_sinister' value='non_sinister'>
                  {t('No siniestradas')}
                </SelectItem>
              </Select>

              {/* ── Metodología ── */}
              <div className='pt-2 border-t border-default-100 dark:border-default-50' />
              <p className='text-sm text-default-500'>
                {t('Metodología')}
              </p>
              <Select
                label={t('Criterio de Fecha')}
                selectedKeys={[draftFilters.dateType]}
                onChange={(e) =>
                  setDraft('dateType', e.target.value as DateTypeChoice)
                }
                className='w-full'
              >
                <SelectItem key='eta' value='eta'>
                  {t('ETA (Fecha de Arribo)')}
                </SelectItem>
                <SelectItem key='etd' value='etd'>
                  {t('ETD (Fecha de Zarpe)')}
                </SelectItem>
                <SelectItem key='sale_date' value='sale_date'>
                  {t('Fecha de Venta')}
                </SelectItem>
              </Select>

              <CostFilterType
                value={draftFilters.cost}
                onChange={(value) => setDraft('cost', value)}
                className='w-full'
                disabled={loading}
                label={t('Metodología Costos')}
              />
              <UnitPriceFilter
                selectedSpecies={selectedSpecies}
                value={
                  kgOrBox === 'box'
                    ? `box-${selectedSpecies?.default_unit_price || 5}`
                    : 'kg'
                }
                onChange={(value) => {
                  setKgOrBox(value === 'kg' ? 'kg' : 'box');
                }}
                className='w-full'
                label={t('Kg o Caja Eq.')}
              />
            </div>
          </div>
        </div>
      </ModalSlideFilter>
      <PageLayout className='relative'>
        <TopLoadingBar
          isLoading={loading || distributionLoading || revenuesLoading}
          ariaLabel={t('Cargando...')}
        />
        <Header
          title={t('Resumen Liquidaciones')}
          description={t(
            'Revisa el consolidado de todas las liquidaciones recibidas, incluyendo FOB total, promedios por caja y detalles por variedad'
          )}
        />

        <div className='sticky -top-6 z-20 py-2 flex items-center gap-2 bg-background -mx-6 px-6'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => {
              if (isFilterOpen) {
                closeFilterModal();
              } else {
                openFilterModal();
              }
            }}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
          {hasAnyExtraRevenue && (
            <div className='flex items-center gap-2'>
              <Switch
                size='sm'
                isSelected={showWithRevenues}
                onValueChange={setShowWithRevenues}
                isDisabled={revenuesLoading}
              >
                <span className='text-sm'>
                  {t('Ver con ingresos adicionales')}
                </span>
              </Switch>
            </div>
          )}
        </div>

        {!loading && settlements.length == 0 && (
          <EmptyState
            className='mb-4'
            title={t('No hay liquidaciones')}
            description={t(
              'No se encontraron liquidaciones para la temporada seleccionada'
            )}
          />
        )}

        {missingDataAlert && (
          <Alert variant='warning' className='mb-4'>
            <h6 className='font-semibold'>
              {t('Liquidaciones incompletas')}
            </h6>
            <p className='mt-2'>
              {t(
                'Algunas de tus liquidaciones no tienen el flete (USD) o el tipo de cambio. Edítalas en'
              )}{' '}
              <Link
                href='/settlements/details'
                className='text-inherit text-sm font-semibold'
              >
                {t('Detalle de Liquidaciones')}
              </Link>{' '}
              {t('para que aparezcan en el resumen.')}
            </p>
          </Alert>
        )}
        {sinisterFilter === 'all' && settlements.some((s) => s.is_sinister) && (
          <Alert className='mb-4'>
            <h6 className='font-semibold'>
              {t('Liquidaciones Siniestradas Incluidas')}
            </h6>
            <p className='mt-2'>
              {t(
                'Hay liquidaciones siniestradas en tus datos, lo que puede afectar el resumen. Puedes filtrarlas usando el filtro "Estado Siniestro" en el panel lateral.'
              )}
            </p>
          </Alert>
        )}

        {/* Hero KPIs */}
        {(() => {
          // Use full-dataset-derived KPIs once settlements arrive; fall back to
          // the lightweight RPC result for instant first paint.
          const hasFullData = settlements.length > 0;
          const displayFob = hasFullData
            ? totalFob
            : (initialKpis?.total_fob ?? 0);
          const displayKgs = hasFullData
            ? totalKgs
            : (initialKpis?.total_kilos ?? 0);
          const displayCount = hasFullData
            ? filteredSettlements.length
            : (initialKpis?.settlement_count ?? 0);
          const displayAdjustedFob = hasFullData
            ? adjustedTotalFob
            : (initialKpis?.total_fob ?? 0);
          const showStrikethrough =
            hasFullData && showWithRevenues && totalRevenueUsd !== 0;
          const averageFob =
            displayKgs > 0 ? (displayFob / displayKgs) * multiplier : 0;
          const adjustedAverageFob =
            displayKgs > 0 ? (displayAdjustedFob / displayKgs) * multiplier : 0;
          const displayedAverageFob = showStrikethrough
            ? adjustedAverageFob
            : averageFob;
          const industryMedianFob =
            globalFobBenchmark?.industry_median_fob != null
              ? globalFobBenchmark.industry_median_fob * multiplier
              : null;
          const previousSeasonAverageFob =
            previousSeasonDistribution &&
            previousSeasonDistribution.grand_totals.total_kilos > 0
              ? (previousSeasonDistribution.grand_totals.total_fob /
                  previousSeasonDistribution.grand_totals.total_kilos) *
                multiplier
              : null;
          const fobVariance =
            industryMedianFob != null
              ? displayedAverageFob - industryMedianFob
              : null;
          const fobVariancePct =
            fobVariance != null && industryMedianFob
              ? fobVariance / industryMedianFob
              : null;
          const percentile = globalFobBenchmark?.own_percentile;
          const benchmarkTone =
            fobVariance == null
              ? 'default'
              : fobVariance >= 0
                ? 'success'
                : 'danger';
          const previousSeasonVariance =
            previousSeasonAverageFob != null
              ? displayedAverageFob - previousSeasonAverageFob
              : null;
          const previousSeasonVariancePct =
            previousSeasonVariance != null && previousSeasonAverageFob
              ? previousSeasonVariance / previousSeasonAverageFob
              : null;
          const previousSeasonTone =
            previousSeasonVariance == null
              ? 'default'
              : previousSeasonVariance >= 0
                ? 'success'
                : 'danger';

          if (kpisLoading && !hasFullData) return <KPICardsSkeleton />;

          return (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
              <Card className='w-full p-2'>
                <CardBody className='px-5 pt-5 pb-5'>
                  <div className='grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-stretch h-full'>
                    <div className='min-w-0 flex flex-col justify-between h-full space-y-3'>
                      <p className='text-sm text-default-400 leading-none'>
                        {t('FOB Total Liquidado')}
                      </p>
                      {showStrikethrough ? (
                        <div className='flex items-baseline gap-3'>
                          <span className='text-2xl font-semibold text-green-600 dark:text-green-400'>
                            {formatMoneyUSD(displayAdjustedFob, {
                              decimals: 0,
                            })}
                          </span>
                          <span className='line-through text-default-300 text-lg'>
                            {formatMoneyUSD(displayFob, { decimals: 0 })}
                          </span>
                        </div>
                      ) : (
                        <p className='text-2xl font-semibold'>
                          {formatMoneyUSD(displayFob, { decimals: 0 })}
                        </p>
                      )}
                      <p className='text-sm text-default-500'>
                        {displayCount} {t('liquidaciones')} ·{' '}
                        {formatNumber(displayKgs, { decimals: 0 })} kg ·{' '}
                        {formatNumber(
                          displayKgs /
                            (selectedSpecies?.default_unit_price || 5),
                          { decimals: 0 }
                        )}{' '}
                        {t('cajas eq.')}{' '}
                        {selectedSpecies?.default_unit_price || 5}kg
                      </p>
                    </div>

                    <div className='w-full border-t border-default-200 pt-5 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-4 flex flex-col justify-center h-full'>
                      {shipmentSettlementCountsLoading ? (
                        <div className='space-y-2'>
                          <Skeleton className='h-5 w-full rounded-md' />
                          <Skeleton className='h-4 w-4/5 rounded-md' />
                        </div>
                      ) : !shipmentSettlementCounts ||
                        shipmentSettlementCounts.total_shipments === 0 ? (
                        <p className='text-xs text-default-400'>
                          {t('Despachos no disponibles')}
                        </p>
                      ) : (
                        (() => {
                          const total =
                            shipmentSettlementCounts.total_shipments;
                          const settled =
                            shipmentSettlementCounts.settled_shipments;
                          const pct = total > 0 ? (settled / total) * 100 : 0;
                          const displayPct = formatCompletionPercent(
                            settled,
                            total
                          );
                          const missingSettlementReferences =
                            shipmentSettlementCounts.missing_settlement_references ||
                            [];
                          const missingSettlementReferencesCount =
                            shipmentSettlementCounts.missing_settlement_references_count ||
                            missingSettlementReferences.length;
                          return (
                            <div className='space-y-3'>
                              <div className='flex items-center justify-between gap-3'>
                                <div className='min-w-0 space-y-1'>
                                  <p className='text-sm font-semibold text-default-600'>
                                    {t('% Liquidadas')}
                                  </p>
                                  <p className='text-xs text-default-400 tabular-nums'>
                                    {settled} {t('de')} {total} {t('despachos')}
                                  </p>
                                </div>
                                <p className='text-2xl font-bold tabular-nums text-green-600 dark:text-green-400'>
                                  {displayPct}%
                                </p>
                              </div>
                              <Progress
                                value={pct}
                                color='success'
                                size='md'
                                className='w-full'
                              />
                              {missingSettlementReferencesCount > 0 && (
                                <Alert variant='warning'>
                                  <p className='text-xs font-semibold'>
                                    {missingSettlementReferencesCount}{' '}
                                    {t(
                                      'liquidaciones aprobadas no están en despachos'
                                    )}
                                  </p>
                                  {missingSettlementReferences.length > 0 && (
                                    <p className='mt-1 text-xs font-normal'>
                                      {missingSettlementReferences
                                        .slice(0, 5)
                                        .join(', ')}
                                      {missingSettlementReferencesCount > 5 &&
                                        ` ${t('y')} ${
                                          missingSettlementReferencesCount - 5
                                        } ${t('más')}`}
                                    </p>
                                  )}
                                </Alert>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className='w-full p-2'>
                <CardBody className='px-5 pt-5 pb-5'>
                  <div className='grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-stretch h-full'>
                    <div className='min-w-0 flex flex-col justify-between h-full space-y-3'>
                      <p className='text-sm text-default-400 leading-none'>
                        {kgOrBox === 'kg'
                          ? t('Promedio FOB')
                          : `${t('Promedio FOB')}`}
                      </p>
                      {showStrikethrough ? (
                        <div className='flex items-baseline gap-3'>
                          <span className='text-2xl font-semibold text-green-600 dark:text-green-400'>
                            {formatMoneyUSD(adjustedAverageFob, {
                              decimals: 2,
                            })}
                          </span>
                          <span className='line-through text-default-300 text-lg'>
                            {formatMoneyUSD(averageFob, { decimals: 2 })}
                          </span>
                        </div>
                      ) : (
                        <p className='text-2xl font-semibold'>
                          {formatMoneyUSD(averageFob, { decimals: 2 })}
                        </p>
                      )}
                      <p className='text-sm text-default-500'>
                        {kgOrBox === 'kg'
                          ? `${t('por kilogramo')}`
                          : `${t('por caja equivalente')} ${selectedSpecies?.default_unit_price || 5}kg`}
                      </p>
                    </div>

                    <div className='w-full border-t border-default-200 pt-5 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-4 flex flex-col h-full'>
                      <div className='flex-1 flex flex-col justify-around gap-3'>
                        {globalFobBenchmarkLoading ? (
                          <div className='space-y-2'>
                            <Skeleton className='h-5 w-full rounded-md' />
                            <Skeleton className='h-4 w-4/5 rounded-md' />
                          </div>
                        ) : industryMedianFob == null ? (
                          <p className='text-xs text-default-400'>
                            {t('Benchmark industria no disponible')}
                          </p>
                        ) : (
                          <div className='flex items-center justify-between gap-3'>
                            <div className='min-w-0 space-y-1'>
                              <p className='text-sm font-semibold text-default-600'>
                                {t('vs industria')}
                              </p>
                              <p className='text-xs text-default-400 tabular-nums'>
                                {t('Mediana')}{' '}
                                {formatMoneyUSD(industryMedianFob, {
                                  decimals: 2,
                                })}
                                {percentile != null
                                  ? ` · P${Number(percentile).toFixed(0)}`
                                  : ''}
                              </p>
                            </div>
                            <div className='flex flex-col items-end gap-1'>
                              <Chip
                                size='md'
                                radius='sm'
                                color={
                                  benchmarkTone === 'success'
                                    ? 'success'
                                    : benchmarkTone === 'danger'
                                      ? 'danger'
                                      : 'default'
                                }
                                variant='flat'
                                startContent={
                                  fobVariance == null ? null : fobVariance >=
                                    0 ? (
                                    <Icon icon='tabler:arrow-up' className='text-sm' />
                                  ) : (
                                    <Icon icon='tabler:arrow-down' className='text-sm' />
                                  )
                                }
                                classNames={{
                                  content: 'font-semibold tabular-nums text-sm',
                                }}
                              >
                                {fobVariancePct != null
                                  ? `${fobVariancePct >= 0 ? '+' : ''}${(
                                      fobVariancePct * 100
                                    ).toFixed(1)}%`
                                  : '-'}
                              </Chip>
                              <span
                                className={`text-xs font-semibold tabular-nums ${
                                  benchmarkTone === 'success'
                                    ? 'text-green-600 dark:text-green-400'
                                    : benchmarkTone === 'danger'
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-default-500'
                                }`}
                              >
                                {fobVariance == null
                                  ? ''
                                  : `${fobVariance >= 0 ? '+' : ''}${formatMoneyUSD(
                                      fobVariance,
                                      { decimals: 2 }
                                    )}`}
                              </span>
                            </div>
                          </div>
                        )}

                        {previousSeasonKpisLoading ? (
                          <div className='border-t border-default-100 pt-3 space-y-2'>
                            <Skeleton className='h-5 w-full rounded-md' />
                            <Skeleton className='h-4 w-4/5 rounded-md' />
                          </div>
                        ) : previousSeasonAverageFob != null ? (
                          <div className='flex items-center justify-between gap-3 border-t border-default-100 pt-3'>
                            <div className='min-w-0 space-y-1'>
                              <p className='text-sm font-semibold text-default-600'>
                                {t('vs temp. anterior')}
                              </p>
                              <p className='text-xs text-default-400 tabular-nums'>
                                {t('Promedio')}{' '}
                                {formatMoneyUSD(previousSeasonAverageFob, {
                                  decimals: 2,
                                })}
                              </p>
                            </div>
                            <div className='flex flex-col items-end gap-1'>
                              <Chip
                                size='md'
                                radius='sm'
                                color={
                                  previousSeasonTone === 'success'
                                    ? 'success'
                                    : previousSeasonTone === 'danger'
                                      ? 'danger'
                                      : 'default'
                                }
                                variant='flat'
                                startContent={
                                  previousSeasonVariance ==
                                  null ? null : previousSeasonVariance >= 0 ? (
                                    <Icon icon='tabler:arrow-up' className='text-sm' />
                                  ) : (
                                    <Icon icon='tabler:arrow-down' className='text-sm' />
                                  )
                                }
                                classNames={{
                                  content: 'font-semibold tabular-nums text-sm',
                                }}
                              >
                                {previousSeasonVariancePct != null
                                  ? `${previousSeasonVariancePct >= 0 ? '+' : ''}${(
                                      previousSeasonVariancePct * 100
                                    ).toFixed(1)}%`
                                  : '-'}
                              </Chip>
                              <span
                                className={`text-xs font-semibold tabular-nums ${
                                  previousSeasonTone === 'success'
                                    ? 'text-green-600 dark:text-green-400'
                                    : previousSeasonTone === 'danger'
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-default-500'
                                }`}
                              >
                                {previousSeasonVariance == null
                                  ? ''
                                  : `${previousSeasonVariance >= 0 ? '+' : ''}${formatMoneyUSD(
                                      previousSeasonVariance,
                                      { decimals: 2 }
                                    )}`}
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          );
        })()}

        {/* ═══════════════ FOB ═══════════════ */}
        <Divider className='my-8' />
        <h2 className='text-lg font-semibold mb-4 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
          {t('FOB')}
        </h2>

        {/* Tabs always visible — they only switch local state. */}
        <div className='overflow-x-auto'>
          <div className='min-w-[768px]'>
            <Tabs
              aria-label={t('FOB')}
              selectedKey={distributionTab}
              onSelectionChange={(key) => setDistributionTab(key as string)}
              className='mb-4'
              size='lg'
              variant='underlined'
            >
              <Tab key='client' title={t('Por Cliente')} />
              <Tab key='variety' title={t('Por Variedad')} />
              <Tab key='caliber' title={t('Por Calibre')} />
              <Tab key='format' title={t('Por Formato')} />
              {selectedSpecies?.has_color && (
                <Tab key='color' title={t('Por Color')} />
              )}
              <Tab key='country' title={t('Por País')} />
              <Tab key='market' title={t('Por Mercado')} />
              <Tab key='label' title={t('Por Etiqueta')} />
              <Tab key='fleet' title={t('Por Tipo de Flete')} />
              <Tab key='business_type' title={t('Por Tipo de Negocio')} />
            </Tabs>
          </div>
        </div>

        {/* Chip filters + Acumulado: gated on distributionLoading (~3s). The
            chip filter options come from filter_universe so they don't need
            the heavier monolithic settlements payload to render. */}
        {distributionLoading || !distributionData ? (
          <SectionSkeleton rows={2} />
        ) : (
          <ChartsFiltersSection
            settlements={filtersInputSettlements}
            primaryFilter={
              distributionTab === 'client'
                ? 'receiver'
                : (distributionTab as any)
            }
            filters={chartFilters}
            onFilterChange={handleChartFilterChange}
            onClearFilters={handleClearChartFilters}
          />
        )}

        {distributionLoading || !distributionData ? (
          <div className='mt-6 mb-8'>
            <SectionSkeleton rows={5} />
          </div>
        ) : (
          <div className='mt-6 mb-8'>
            <div className='flex items-center justify-between mb-2'>
              <h3 className='text-base font-semibold'>{t('Acumulado')}</h3>
              <ExcelExportButton
                onClick={() => acumuladoTableRef.current?.exportToExcel()}
              />
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4'>
              <Card className='w-full'>
                <CardBody className='p-0'>
                  {distributionTab === 'client' ? (
                    <ClientSettlementsTable
                      ref={acumuladoTableRef}
                      settlements={
                        !distributionData || chipFiltersNarrowing
                          ? chartFilteredSettlements
                          : undefined
                      }
                      aggregated={
                        distributionData && !chipFiltersNarrowing
                          ? distributionData.groupings.client.map((row) => ({
                              key: row.key,
                              total_fob: row.total_fob,
                              total_kilos: row.total_kilos,
                              total_extra_revenue_usd:
                                row.total_extra_revenue_usd,
                              settlement_count: row.settlement_count,
                            }))
                          : undefined
                      }
                      boxSize={selectedSpecies?.default_unit_price || 5}
                      kgOrBox={kgOrBox}
                      showWithRevenues={showWithRevenues}
                    />
                  ) : (
                    <SettlementsVarietyFOBTable
                      ref={acumuladoTableRef}
                      settlements={
                        !varietyTableAggregated || chipFiltersNarrowing
                          ? chartFilteredSettlements
                          : undefined
                      }
                      aggregated={
                        varietyTableAggregated && !chipFiltersNarrowing
                          ? varietyTableAggregated
                          : undefined
                      }
                      kgOrBox={kgOrBox}
                      boxSize={selectedSpecies?.default_unit_price || 5}
                      groupBy={distributionTab}
                      showWithRevenues={showWithRevenues}
                    />
                  )}
                </CardBody>
              </Card>
              <Card className='w-full p-2'>
                <CardBody className='p-4 max-h-[600px] overflow-y-auto'>
                  <DistributionBarChart
                    data={
                      (chipFiltersNarrowing
                        ? distributionChartData
                        : (distributionBarChartDataFromRPC ??
                          distributionChartData))[
                        distributionTab as keyof typeof distributionChartData
                      ] || []
                    }
                    unitLabel={
                      kgOrBox === 'kg'
                        ? 'FOB/KG'
                        : `FOB/CEQ ${selectedSpecies?.default_unit_price || 5}KG`
                    }
                    formatValue={(v) => formatMoneyUSD(v, { decimals: 2 })}
                  />
                </CardBody>
              </Card>
            </div>
          </div>
        )}

        {/* Evolutivo depends on monolithic settlements (timeline data not in
            distribution RPC), so it stays gated on `loading` (~7s). */}
        {loading ? (
          <SectionSkeleton rows={5} />
        ) : (
          <ChartsSection
            settlements={adjustedFilteredSettlements}
            chartViewMode={chartsViewMode}
            chartOrTable={chartOrTable}
            onChartViewModeChange={setChartsViewMode}
            onChartOrTableChange={setChartOrTable}
            boxSize={selectedSpecies?.default_unit_price || 5}
            dateType={dateType}
            selectedTab={distributionTab}
            filters={chartFilters}
            valueMode={chartValueMode}
            onValueModeChange={setChartValueMode}
            multiplier={multiplier}
          />
        )}

        {/* ═══════════════ Análisis Comparativo ═══════════════ */}
        <Divider className='my-8' />
        <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
          {t('Análisis Comparativo')}
        </h2>

        {loading ? (
          <div className='flex flex-col gap-4 mb-2'>
            <SectionSkeleton rows={4} />
            <SectionSkeleton rows={4} />
          </div>
        ) : (
          <div className='flex flex-col gap-6 mb-2'>
            <div>
              <FilteredDataNotice chips={activeFilterChips} />
              <RankingClientsTable
                settlements={adjustedFilteredSettlements}
                sinisterFilter={sinisterFilter}
                originalSettlements={originalSettlements}
                speciesId={selectedSpecies?.id}
                season={selectedSeason}
                includeRevenues={showWithRevenues}
                receiversFilter={
                  receiverFilter.includes('all') ? undefined : receiverFilter
                }
                marketsFilter={
                  canonicalMarketFilter.includes('all')
                    ? undefined
                    : canonicalMarketFilter
                }
                costMethodology={costFilter}
              />
            </div>
            <div>
              <FilteredDataNotice chips={activeFilterChips} />
              <ClientComparativeSaldosTable
                settlements={adjustedFilteredSettlements}
              />
            </div>
          </div>
        )}

        {/* ═══════════════ Ventas ═══════════════ */}
        {/* <Divider className='my-8' />
        <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>{t('Ventas')}</h2>
        <Card className='w-full p-2'>
          <CardBody className='p-8 flex items-center justify-center'>
            <p className='text-default-400 text-sm'>{t('Próximamente')}</p>
          </CardBody>
        </Card> */}

        {/* ═══════════════ Flujos ═══════════════ */}
        <Divider className='my-8' />
        <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
          {t('Flujos')}
        </h2>

        {loading ? (
          <SectionSkeleton rows={5} />
        ) : (
          <>
            <div className='mb-4'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold'>
                  {t('Tabla de Flujos')}
                </h3>
                <ExcelExportButton
                  onClick={() => flowsTableRef.current?.exportToExcel()}
                />
              </div>
              <div className='overflow-x-auto'>
                <div className='min-w-[768px]'>
                  <Tabs
                    aria-label={t('Flujos')}
                    selectedKey={flowsTab}
                    onSelectionChange={(key) => setFlowsTab(key as string)}
                    className='mb-4'
                    size='lg'
                    variant='underlined'
                  >
                    <Tab key='client' title={t('Por Cliente')} />
                    <Tab key='variety' title={t('Por Variedad')} />
                    <Tab key='country' title={t('Por País')} />
                  </Tabs>
                </div>
              </div>
              <Card className='w-full'>
                <CardBody className='p-0'>
                  <FlowsTable
                    ref={flowsTableRef}
                    settlements={adjustedFilteredSettlements}
                    activeTab={flowsTab}
                  />
                </CardBody>
              </Card>
            </div>

            {/* ═══════════════ Costos ═══════════════ */}
            <Divider className='my-8' />
            <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
              {t('Costos')}
            </h2>

            <div className='mb-4'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold'>{t('Resumen Costos')}</h3>
                <div className='flex items-center gap-2'>
                  <Tabs
                    aria-label={t('Valor costos')}
                    selectedKey={costValueMode}
                    onSelectionChange={(key) =>
                      setCostValueMode(key as 'average' | 'total')
                    }
                    size='sm'
                  >
                    <Tab key='total' title={t('Total')} />
                    <Tab key='average' title={t('Promedios')} />
                  </Tabs>
                  <ExcelExportButton
                    onClick={() => costsTableRef.current?.exportToExcel()}
                  />
                </div>
              </div>
              <Card className='w-full'>
                <CardBody className='p-0'>
                  <ClientCostsTable
                    ref={costsTableRef}
                    settlements={adjustedFilteredSettlements}
                    kgOrBox={kgOrBox}
                    costValueMode={costValueMode}
                  />
                </CardBody>
              </Card>
            </div>

            {/* ═══════════════ Tipos de Cambio ═══════════════ */}
            <Divider className='my-8' />
            <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
              {t('Tipos de Cambio')}
            </h2>

            <div className='mb-4'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-base font-semibold'>{t('Promedios')}</h3>
                <ExcelExportButton
                  onClick={() => exchangeRatesTableRef.current?.exportToExcel()}
                />
              </div>
              <Card className='w-full'>
                <CardBody className='p-0'>
                  <ExchangeRatesTable
                    ref={exchangeRatesTableRef}
                    settlements={adjustedFilteredSettlements}
                  />
                </CardBody>
              </Card>
            </div>

            <div className='mb-4'>
              <ExchangeRateEvolutionChart
                settlements={adjustedFilteredSettlements}
                receiverFilter={receiverFilter}
              />
            </div>

            {/* ═══════════════ Resumen Resultados ═══════════════ */}
            <Divider className='my-8' />
            <h2 className='text-lg font-semibold mb-6 sticky top-4 z-[15] bg-background py-2 -mt-2 -mx-6 px-6'>
              {t('Resumen Resultados')}
            </h2>

            <div className='flex flex-col gap-4'>
              {selectedSpecies?.has_color && (
                <div>
                  <div className='flex items-center justify-between mb-3'>
                    <h3 className='text-base font-semibold'>
                      {t('FOB por Calibre y Color')}
                    </h3>
                    <ExcelExportButton
                      onClick={() =>
                        caliberColorTableRef.current?.exportToExcel()
                      }
                    />
                  </div>
                  <FOBAccumulatedCaliberColorTable
                    ref={caliberColorTableRef}
                    settlements={fobScaledSettlements}
                  />
                </div>
              )}
              <div>
                <div className='flex items-center justify-between mb-3'>
                  <h3 className='text-base font-semibold'>
                    {t('FOB por Semana y Variedad')}
                  </h3>
                  <ExcelExportButton
                    onClick={() => weekVarietyTableRef.current?.exportToExcel()}
                  />
                </div>
                <FOBAccumulatedWeekVariety
                  ref={weekVarietyTableRef}
                  settlements={fobScaledSettlements}
                  dateType={dateType}
                />
              </div>
            </div>

            {settlementsForCosts.length > 0 && null}
          </>
        )}
      </PageLayout>
    </div>
  );
};

export default SettlementsSummaryPage;
